import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const LANGUAGES = [
  { code: 'nl', flag: '🇳🇱', label: 'NL', name: 'Nederlands' },
  { code: 'en', flag: '🇬🇧', label: 'EN', name: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'ES', name: 'Español' },
];

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Update hreflang meta tags dynamically
  useEffect(() => {
    const updateHreflang = () => {
      const base = `${window.location.origin}${window.location.pathname}`;
      ['nl', 'en', 'es'].forEach(lang => {
        let el = document.querySelector<HTMLLinkElement>(`link[hreflang="${lang}"]`);
        if (!el) {
          el = document.createElement('link');
          el.rel = 'alternate';
          el.hreflang = lang;
          document.head.appendChild(el);
        }
        el.href = `${base}?lang=${lang}`;
      });
    };
    updateHreflang();
  }, [i18n.language]);

  const handleChange = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('zpiritualz_lang', code);
    // Reflect in URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('lang', code);
    window.history.replaceState({}, '', url.toString());
    // Update html lang attribute for accessibility + SEO
    document.documentElement.lang = code;
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Taal kiezen / Select language"
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-500/20 text-white/65 hover:text-white hover:border-violet-500/45 transition-all cursor-pointer whitespace-nowrap"
      >
        <span className="text-lg leading-none">{currentLang.flag}</span>
        <span className="text-xs font-semibold tracking-wide">{currentLang.label}</span>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 bg-[#0e001a] border border-violet-500/25 rounded-xl overflow-hidden z-50"
          style={{ minWidth: '140px', boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }}
        >
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleChange(lang.code)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                i18n.language === lang.code
                  ? 'text-violet-300 bg-violet-500/15'
                  : 'text-white/55 hover:bg-violet-500/10 hover:text-white'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.name}</span>
              {i18n.language === lang.code && (
                <div className="w-3 h-3 flex items-center justify-center shrink-0">
                  <i className="ri-check-line text-violet-400 text-xs" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
